import { jsxs, jsx } from "react/jsx-runtime";
import { useState, useRef, useEffect } from "react";
import { useGraphiQLActions, useGraphiQL, pick, PanelHeader, Dialog, Button } from "@graphiql/react";
import { useCollectionsStore, collectionsStore } from "../store.js";
import { CollectionRow } from "./collection-row.js";
import { ImportDialog } from "./import-dialog.js";
import { ExportDialog } from "./export-dialog.js";
import { ConflictDialog } from "./conflict-dialog.js";
import SvgDownload from "../icons/download.js";
import SvgUpload from "../icons/upload.js";
function parseCollectionsExport(text) {
  try {
    const parsed = JSON.parse(text);
    const list = Array.isArray(parsed == null ? void 0 : parsed.collections) ? parsed.collections : Array.isArray(parsed) ? parsed : null;
    if (!(list == null ? void 0 : list.length) || !list.every(
      (c) => c && typeof c === "object" && Array.isArray(c.items)
    )) {
      return null;
    }
    return list.length;
  } catch {
    return null;
  }
}
function isEditableTarget(el) {
  if (!el) {
    return false;
  }
  return el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable || Boolean(el.closest(".monaco-editor"));
}
function deriveSourceLabel(incoming) {
  if (incoming.length === 1 && incoming[0]) {
    return incoming[0].name;
  }
  return `${incoming.length} collections`;
}
const CollectionsPanel = ({
  readOnly = false,
  allowImportExport = true,
  allowReplace = true
}) => {
  const allowImport = !readOnly && allowImportExport;
  const actions = useCollectionsStore((s) => s.actions);
  const collections = useCollectionsStore((s) => s.collections);
  const loaded = useCollectionsStore((s) => s.loaded);
  const [openDialog, setOpenDialog] = useState(
    null
  );
  const [isDragOver, setIsDragOver] = useState(false);
  const [status, setStatus] = useState(
    null
  );
  const [pendingConflict, setPendingConflict] = useState(null);
  const [pendingReplace, setPendingReplace] = useState(null);
  const [collapsed, setCollapsed] = useState(/* @__PURE__ */ new Set());
  const [grabbed, setGrabbed] = useState(null);
  const [origin, setOrigin] = useState(null);
  const [liveMessage, setLiveMessage] = useState("");
  const listRef = useRef(null);
  const cancelledItemRef = useRef(null);
  useEffect(() => {
    if (!status) {
      return;
    }
    const timer = setTimeout(() => setStatus(null), 4e3);
    return () => clearTimeout(timer);
  }, [status]);
  const applyReplace = (text) => {
    const analysis = collectionsStore.getState().actions.analyzeImport(text);
    collectionsStore.getState().actions.applyImport(analysis, { mode: "replace" });
  };
  const runImport = (text, mode) => {
    if (mode === "replace") {
      setPendingReplace(text);
      return;
    }
    const count = parseCollectionsExport(text);
    if (count === null) {
      setStatus({
        ok: false,
        message: "That file isn't a collections export."
      });
      return;
    }
    const analysis = collectionsStore.getState().actions.analyzeImport(text);
    if (!analysis.ok) {
      setStatus({ ok: false, message: "Invalid collections export." });
      return;
    }
    if (analysis.changedItems.length === 0) {
      collectionsStore.getState().actions.applyImport(analysis, { mode: "merge", applyChanges: true });
      return;
    }
    setPendingConflict({
      analysis,
      sourceLabel: deriveSourceLabel(analysis._incoming)
    });
  };
  const handleConflictResolve = (resolution) => {
    if (!pendingConflict) {
      return;
    }
    const { analysis } = pendingConflict;
    collectionsStore.getState().actions.applyImport(analysis, resolution);
    setPendingConflict(null);
  };
  const importDroppedFile = async (file) => {
    const text = await file.text();
    if (parseCollectionsExport(text) === null) {
      setStatus({
        ok: false,
        message: "That file isn't a collections export."
      });
      return;
    }
    runImport(text, "merge");
  };
  const allowImportRef = useRef(allowImport);
  allowImportRef.current = allowImport;
  useEffect(() => {
    const onPaste = (event) => {
      var _a;
      if (!allowImportRef.current) {
        return;
      }
      if (isEditableTarget(document.activeElement)) {
        return;
      }
      const text = ((_a = event.clipboardData) == null ? void 0 : _a.getData("text")) ?? "";
      if (parseCollectionsExport(text) === null) {
        return;
      }
      event.preventDefault();
      runImport(text, "merge");
    };
    document.addEventListener("paste", onPaste);
    return () => document.removeEventListener("paste", onPaste);
  }, []);
  const { addTab, changeTab, updateActiveTabValues } = useGraphiQLActions();
  const { queryEditor, variableEditor, headerEditor } = useGraphiQL(
    pick("queryEditor", "variableEditor", "headerEditor")
  );
  const tabs = useGraphiQL((s) => s.tabs);
  const activeTabId = useGraphiQL((s) => {
    var _a;
    return (_a = s.tabs[s.activeTabIndex]) == null ? void 0 : _a.id;
  });
  const pendingLink = useRef(
    null
  );
  useEffect(() => {
    const link = pendingLink.current;
    if (link && activeTabId) {
      actions.linkTab(activeTabId, link.collectionId, link.itemId);
      pendingLink.current = null;
    }
  }, [activeTabId, actions]);
  const handleOpen = (item) => {
    var _a;
    const { links } = collectionsStore.getState();
    const openIndex = tabs.findIndex(
      (t) => {
        var _a2;
        return t.id && ((_a2 = links[t.id]) == null ? void 0 : _a2.itemId) === item.id;
      }
    );
    if (openIndex !== -1) {
      changeTab(openIndex);
      return;
    }
    const collectionId = (_a = collections.find(
      (c) => c.items.some((i) => i.id === item.id)
    )) == null ? void 0 : _a.id;
    if (collectionId) {
      pendingLink.current = { collectionId, itemId: item.id };
    }
    addTab();
    queryEditor == null ? void 0 : queryEditor.setValue(item.query ?? "");
    variableEditor == null ? void 0 : variableEditor.setValue(item.variables ?? "");
    headerEditor == null ? void 0 : headerEditor.setValue(item.headers ?? "");
    updateActiveTabValues({
      query: item.query,
      variables: item.variables ?? "",
      headers: item.headers ?? "",
      lastSavedQuery: item.query ?? null
    });
  };
  const announce = (message) => setLiveMessage(message);
  const handleShareCollection = async (collectionId) => {
    const json = collectionsStore.getState().actions.exportCollection(collectionId);
    await navigator.clipboard.writeText(json);
  };
  const handleShareItem = async (itemId) => {
    const json = collectionsStore.getState().actions.exportItem(itemId);
    await navigator.clipboard.writeText(json);
  };
  const handleRename = actions.renameCollection;
  const handleDelete = actions.deleteCollection;
  const handleDeleteItem = actions.deleteItem;
  const handleMoveItem = actions.moveItem;
  const handleAddItem = actions.addItem;
  const handleRenameItem = (collectionId, itemId, updates) => {
    collectionsStore.getState().actions.updateItem(collectionId, itemId, {
      name: updates.name,
      description: updates.description || void 0
    });
  };
  const toggleExpand = (id) => setCollapsed((prev) => {
    const next = new Set(prev);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    return next;
  });
  const expand = (id) => setCollapsed((prev) => {
    if (!prev.has(id)) {
      return prev;
    }
    const next = new Set(prev);
    next.delete(id);
    return next;
  });
  const handleGrabToggle = (collectionId, index, itemId) => {
    var _a;
    if (grabbed) {
      const cols = collectionsStore.getState().collections;
      const col = cols.find((c) => c.id === grabbed.collectionId);
      const droppedItem = col == null ? void 0 : col.items[grabbed.index];
      setLiveMessage(
        `Dropped ${(droppedItem == null ? void 0 : droppedItem.name) ?? grabbed.itemId} in ${(col == null ? void 0 : col.name) ?? ""} at position ${grabbed.index + 1}.`
      );
      setGrabbed(null);
      setOrigin(null);
      return;
    }
    setGrabbed({ collectionId, index, itemId });
    setOrigin({ collectionId, index });
    const item = (_a = collectionsStore.getState().collections.find((c) => c.id === collectionId)) == null ? void 0 : _a.items[index];
    setLiveMessage(
      `Grabbed ${(item == null ? void 0 : item.name) ?? itemId}. Use arrow keys to move, space to drop, escape to cancel.`
    );
  };
  const handleGrabMove = (direction) => {
    if (!grabbed) {
      return;
    }
    const cols = collectionsStore.getState().collections;
    const cIdx = cols.findIndex((c) => c.id === grabbed.collectionId);
    const col = cols[cIdx];
    if (!col) {
      return;
    }
    const { moveItem } = collectionsStore.getState().actions;
    if (direction === "down") {
      if (grabbed.index < col.items.length - 1) {
        moveItem(col.id, grabbed.index, col.id, grabbed.index + 1);
        const next = { ...grabbed, index: grabbed.index + 1 };
        setGrabbed(next);
        setLiveMessage(
          `Position ${next.index + 1} of ${col.items.length} in ${col.name}.`
        );
      } else if (cIdx < cols.length - 1) {
        const dest = cols[cIdx + 1];
        expand(dest.id);
        moveItem(col.id, grabbed.index, dest.id, 0);
        setGrabbed({ collectionId: dest.id, index: 0, itemId: grabbed.itemId });
        setLiveMessage(
          `Moved to ${dest.name}, position 1 of ${dest.items.length + 1}.`
        );
      } else {
        setLiveMessage("Already at the end.");
      }
    } else if (grabbed.index > 0) {
      moveItem(col.id, grabbed.index, col.id, grabbed.index - 1);
      const next = { ...grabbed, index: grabbed.index - 1 };
      setGrabbed(next);
      setLiveMessage(
        `Position ${next.index + 1} of ${col.items.length} in ${col.name}.`
      );
    } else if (cIdx > 0) {
      const dest = cols[cIdx - 1];
      expand(dest.id);
      const toIndex = dest.items.length;
      moveItem(col.id, grabbed.index, dest.id, toIndex);
      setGrabbed({
        collectionId: dest.id,
        index: toIndex,
        itemId: grabbed.itemId
      });
      setLiveMessage(
        `Moved to ${dest.name}, position ${toIndex + 1} of ${dest.items.length + 1}.`
      );
    } else {
      setLiveMessage("Already at the start.");
    }
  };
  const handleGrabCancel = () => {
    if (!grabbed || !origin) {
      return;
    }
    collectionsStore.getState().actions.moveItem(
      grabbed.collectionId,
      grabbed.index,
      origin.collectionId,
      origin.index
    );
    cancelledItemRef.current = grabbed.itemId;
    setGrabbed(null);
    setOrigin(null);
    setLiveMessage("Move cancelled.");
  };
  useEffect(() => {
    var _a, _b, _c, _d;
    if (grabbed) {
      (_b = (_a = listRef.current) == null ? void 0 : _a.querySelector(
        `[data-collection-drag-handle="${grabbed.itemId}"]`
      )) == null ? void 0 : _b.focus();
    } else if (cancelledItemRef.current) {
      (_d = (_c = listRef.current) == null ? void 0 : _c.querySelector(
        `[data-collection-drag-handle="${cancelledItemRef.current}"]`
      )) == null ? void 0 : _d.focus();
      cancelledItemRef.current = null;
    }
  }, [grabbed]);
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: `graphiql-collections-panel${isDragOver ? " graphiql-collections-drop-active" : ""}`,
      onDragOver: (e) => {
        if (!allowImport || !e.dataTransfer.types.includes("Files")) {
          return;
        }
        e.preventDefault();
        setIsDragOver(true);
      },
      onDragLeave: (e) => {
        if (e.currentTarget.contains(e.relatedTarget)) {
          return;
        }
        setIsDragOver(false);
      },
      onDrop: (e) => {
        if (!allowImport) {
          return;
        }
        const file = e.dataTransfer.files[0];
        if (!file) {
          return;
        }
        e.preventDefault();
        setIsDragOver(false);
        void importDroppedFile(file);
      },
      children: [
        /* @__PURE__ */ jsx(
          PanelHeader,
          {
            title: "Operation Collections",
            subtitle: "Save and organize operations into collections."
          }
        ),
        (!readOnly || allowImportExport) && /* @__PURE__ */ jsxs("div", { className: "graphiql-collections-toolbar", children: [
          /* @__PURE__ */ jsx("div", { className: "graphiql-collections-toolbar-left", children: !readOnly && /* @__PURE__ */ jsx(
            "button",
            {
              type: "button",
              className: "graphiql-collections-action",
              onClick: () => collectionsStore.getState().actions.createCollection("New Collection"),
              "aria-label": "New collection",
              title: "New collection",
              children: "+ New Collection"
            }
          ) }),
          /* @__PURE__ */ jsxs("div", { className: "graphiql-collections-toolbar-right", children: [
            allowImport && /* @__PURE__ */ jsx(
              "button",
              {
                type: "button",
                className: "graphiql-collections-action",
                onClick: () => setOpenDialog("import"),
                "aria-label": "Import collections",
                title: "Import collections",
                children: /* @__PURE__ */ jsx(SvgDownload, {})
              }
            ),
            allowImportExport && /* @__PURE__ */ jsx(
              "button",
              {
                type: "button",
                className: "graphiql-collections-action",
                onClick: () => setOpenDialog("export"),
                "aria-label": "Export collections",
                title: "Export collections",
                children: /* @__PURE__ */ jsx(SvgUpload, {})
              }
            )
          ] })
        ] }),
        status && /* @__PURE__ */ jsx(
          "div",
          {
            className: `graphiql-collections-status${status.ok ? "" : " graphiql-collections-status-error"}`,
            role: "status",
            children: status.message
          }
        ),
        !loaded && /* @__PURE__ */ jsx("div", { className: "graphiql-collections-loading", children: "Loading…" }),
        loaded && collections.length === 0 && /* @__PURE__ */ jsx("div", { className: "graphiql-collections-empty", children: readOnly ? "No collections." : allowImport ? "No collections yet. Save an operation to get started, or drop or paste a collections export to import one." : "No collections yet. Save an operation to get started." }),
        /* @__PURE__ */ jsx(
          "div",
          {
            className: "graphiql-sr-only",
            "aria-live": "assertive",
            "aria-atomic": "true",
            children: liveMessage
          }
        ),
        /* @__PURE__ */ jsx("div", { className: "graphiql-collections-list", ref: listRef, children: collections.map((collection) => /* @__PURE__ */ jsx(
          CollectionRow,
          {
            collection,
            readOnly,
            expanded: !collapsed.has(collection.id),
            onToggleExpand: () => toggleExpand(collection.id),
            grabbed,
            onGrabToggle: handleGrabToggle,
            onGrabMove: handleGrabMove,
            onGrabCancel: handleGrabCancel,
            onRename: handleRename,
            onDelete: handleDelete,
            onShareCollection: handleShareCollection,
            onOpenItem: handleOpen,
            onShare: handleShareItem,
            onAnnounce: announce,
            onDeleteItem: handleDeleteItem,
            onMoveItem: handleMoveItem,
            onAddItem: handleAddItem,
            onRenameItem: handleRenameItem
          },
          collection.id
        )) }),
        allowImportExport && /* @__PURE__ */ jsx(
          ExportDialog,
          {
            open: openDialog === "export",
            onClose: () => setOpenDialog(null)
          }
        ),
        allowImport && /* @__PURE__ */ jsx(
          ImportDialog,
          {
            open: openDialog === "import",
            onClose: () => setOpenDialog(null),
            onImport: runImport,
            allowReplace
          }
        ),
        pendingConflict && /* @__PURE__ */ jsx(
          ConflictDialog,
          {
            analysis: pendingConflict.analysis,
            sourceLabel: pendingConflict.sourceLabel,
            open: true,
            onClose: () => setPendingConflict(null),
            onResolve: handleConflictResolve
          }
        ),
        /* @__PURE__ */ jsxs(
          Dialog,
          {
            open: pendingReplace !== null,
            onOpenChange: (o) => !o && setPendingReplace(null),
            children: [
              /* @__PURE__ */ jsx(Dialog.Header, { children: "Replace all collections?" }),
              /* @__PURE__ */ jsxs(Dialog.Body, { children: [
                /* @__PURE__ */ jsx("p", { className: "graphiql-conflict-summary", children: "This will remove all existing collections and replace them with the imported data. This cannot be undone." }),
                /* @__PURE__ */ jsxs("div", { className: "graphiql-conflict-actions", children: [
                  /* @__PURE__ */ jsx(
                    Button,
                    {
                      type: "button",
                      variant: "primary",
                      onClick: () => {
                        const text = pendingReplace;
                        setPendingReplace(null);
                        if (text !== null) {
                          applyReplace(text);
                        }
                      },
                      children: "Replace"
                    }
                  ),
                  /* @__PURE__ */ jsx(Button, { type: "button", onClick: () => setPendingReplace(null), children: "Cancel" })
                ] })
              ] })
            ]
          }
        )
      ]
    }
  );
};
export {
  CollectionsPanel
};
//# sourceMappingURL=collections-panel.js.map
